import { pan, vDist, zoomAt, type Vec } from '@kanvas/board-core';
import { useEditor } from '../../state/store';
import { DRAG_THRESHOLD, type Gesture, type GestureContext } from '../types';

/**
 * Hand-tool / middle-button / right-button / space-drag panning.
 *
 * The camera is driven from raw screen deltas rather than from board
 * coordinates: converting through the camera while the camera itself is moving
 * feeds the change back into the next frame and the board slides away.
 *
 * Right-button pans share the click with the context menu: `onIdleUp` runs only
 * when the pointer never crossed the drag threshold, so a click still opens
 * the menu and a drag never does.
 */
export function createPanGesture(
  ctx: GestureContext,
  opts?: { onIdleUp?: (ctx: GestureContext) => void },
): Gesture {
  let last: Vec = { ...ctx.pointer.screen };
  const origin: Vec = { ...ctx.pointer.screen };
  let dragging = opts?.onIdleUp == null;

  const begin = () => {
    // Panning by hand ends follow mode, same as zooming does. A right-click
    // that never becomes a drag is not a pan, so follow stays on until then.
    if (useEditor.getState().followingUserId) {
      useEditor.getState().setUi({ followingUserId: null });
    }
  };
  if (dragging) begin();

  return {
    cursor: 'grabbing',
    move(next) {
      if (!dragging) {
        if (vDist(origin, next.pointer.screen) < DRAG_THRESHOLD) return;
        dragging = true;
        begin();
      }
      const dx = next.pointer.screen.x - last.x;
      const dy = next.pointer.screen.y - last.y;
      if (dx === 0 && dy === 0) return;
      last = { ...next.pointer.screen };
      useEditor.getState().setViewport((vp) => pan(vp, dx, dy));
    },
    up(next) {
      if (!dragging) opts?.onIdleUp?.(next);
    },
    cancel() {},
  };
}

/** Pinch-zoom, driven by a two-pointer gesture on touch devices. */
export function applyPinch(centerScreen: Vec, scale: number): void {
  useEditor.getState().setViewport((vp) => zoomAt(vp, vp.zoom * scale, centerScreen));
}
